import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import { ComponentProps, useEffect, useMemo, useState } from "react";
import MediaViewer from "../media/MediaViewer";
import { getMediaUrl, getMediaType, isMediaSourceMissing } from "../../utilities/mediaUtils";
import type { Entity, MediaItem, MediaItemDetailed } from "../../types";
import { CreateOutlined } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import { useConfig } from "../../contexts/ConfigContext";
import { useEntityFormActions } from "../../store/entityForm";
import { useSelectionSelector } from "../../store/selection";
import LoadingIndicator from "../common/LoadingIndicator";
import useAuth from "../../hooks/useAuth";
import { isFulcrumMedia } from "../../utilities/fulcrumLibService";

type MediaPreviewProps = ComponentProps<typeof MediaViewer>;

type EntityMediaOverviewProps = {
  entity: MediaItemDetailed | Entity;
  height?: string | number;
  width?: string | number;
  isLoading?: boolean;
  onFindRelatedItem?: () => void;
  mode?: MediaPreviewProps["mode"];
  mediaPreviewOptions?: Partial<MediaPreviewProps>;
  hideRelationshipButton?: boolean;
};

export default function EntityMediaOverview({
  entity,
  height = "100%",
  width = "100%",
  isLoading,
  onFindRelatedItem,
  mode = "preview",
  mediaPreviewOptions = {},
  hideRelationshipButton = false
}: EntityMediaOverviewProps) {
  const [item, setItem] = useState(entity);
  const { apiEndpoints, alwaysRequestRelatedItemsAndMedia } = useConfig();
  const mediaEndpoint = useMemo(() => {
    return apiEndpoints?.find((endpiont) => endpiont?.tableName === "media");
  }, [apiEndpoints]);

  const queryClient = useQueryClient();
  const { openEditForm } = useEntityFormActions();
  const { showRelationShip, selectedEntityId } = useSelectionSelector();
  const { hasWritePermissions } = useAuth();

  const [isMediaPlaying, setIsMediaPlaying] = useState<boolean | null>(null);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);

  // Sync provided item with local
  useEffect(() => {
    setItem((prev) => {
      // if current item id don't matched new one
      // show loading overlay for new item
      if (prev.id !== entity?.id) {
        setShowLoadingOverlay(true);
      }
      return { ...prev, ...entity };
    });
  }, [entity]);

  // Use "full" for expanded/dialog modes to get the actual media file URL.
  // Using "preview" would return thumbnail URLs, which breaks PDFs, videos, and audio.
  const mediaUrl = useMemo(
    () =>
      getMediaUrl(
        entity as MediaItemDetailed,
        mode === "expanded" || mode === "dialog" ? "full" : mode === "thumbnail" ? "thumbnail" : "preview"
      ),
    [entity, mode]
  );

  async function handleEdit() {
    if (!item || !mediaEndpoint) return;
    openEditForm(mediaEndpoint, item as MediaItem, {
      async onUpdated(updatedItem) {
        // Update local state immediately for responsive UI
        setItem((prev) => ({ ...prev, ...updatedItem }));

        // Invalidate the entity cache so other subscribers (DataItemDetails, etc.)
        // pick up the updated data. Uses queryClient directly instead of
        // useSelectedEntity().refresh() to avoid subscribing to the query on mount.
        if (selectedEntityId && selectedEntityId === item.id) {
          try {
            await queryClient.invalidateQueries({
              queryKey: ["entity", String(selectedEntityId)]
            });
          } catch (error) {
            console.error("[EntityMediaOverview] Error invalidating entity cache after edit:", error);
          }
        }
      }
    });
  }

  function handlePlay() {
    setIsMediaPlaying(true);
  }
  function handlePause() {
    setIsMediaPlaying(false);
  }

  const mediaType = getMediaType((item as any) || {});

  // Uploads that fail server-side leave a media row with no url/thumbnail/remote_details.
  // MediaViewer renders a broken-media placeholder for those, so suppressing the loading
  // overlay here keeps the edit (and therefore delete) controls reachable.
  const isMediaUnavailable = useMemo(() => isMediaSourceMissing(entity as MediaItemDetailed), [entity]);

  // Fulcrum-sourced media has no media-API record, so "Find Related" would fetch
  // against the media endpoint and 404. Detect it here to suppress the button.
  const isFulcrumSourcedMedia = useMemo(() => isFulcrumMedia(item), [item]);

  /**
   * show `find related` button:
   *    - always when the relationship drawer is closed (!showRelationShip) so the
   *      user can reopen it after "Back to grid" even with alwaysRequestRelatedItemsAndMedia on.
   *    - when viewing a different entity's media while the drawer is open (carousel navigation).
   *    - never when hideRelationshipButton is true or onFindRelatedItem is not wired.
   *    - never for Fulcrum-sourced media (no media-API record → find-related 404).
   **/
  const showRelatedBtn =
    !hideRelationshipButton && !isFulcrumSourcedMedia && (!showRelationShip || selectedEntityId?.toString() !== item?.id?.toString());

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "minmax(0, 1fr)",
          height: "100%",
          gap: "1.25rem",
          position: "relative",
          overflow: "hidden"
        }}
        // sx={{ height: "100%", position: 'relative', overflow: 'hidden' }}
        className='entity-overview-container'
      >
        {/* Media Viewer */}
        <Box
          className={isLoading || (showLoadingOverlay && !isMediaUnavailable) ? "stale" : ""}
          sx={{ position: "relative", overflow: "hidden" }}
        >
          <MediaViewer
            url={mediaUrl}
            item={entity as MediaItemDetailed}
            height={height}
            width={width}
            mode={mode}
            onPlay={handlePlay}
            onPause={handlePause}
            {...mediaPreviewOptions}
            controlProps={{
              sx: {
                top: "1rem!important",
                right: "3rem",
                zIndex: 3
              }
            }}
            onLoad={() => {
              // hide overlay after successful media load
              setShowLoadingOverlay(false);
            }}
            onError={() => {
              setShowLoadingOverlay(false);
            }}
          />
        </Box>

        {/* Basic Entity meta detail and `Find related` button */}
        <Box
          component='div'
          sx={{
            position: "absolute",
            width: "100%",
            display: "flex",
            maxWidth: width,
            gap: "1.5rem",
            justifyContent: "space-between",
            pointerEvents: "auto",
            flexWrap: "wrap",
            bottom: 0,
            left: 0,
            // Above the loading overlay below — otherwise the edit (and hence delete)
            // action is buried whenever media is loading or never resolves.
            zIndex: 3,
            background: "#2424249c",
            padding: "1rem",
            transition: "300ms ease-in-out",
            ...(isMediaPlaying && mediaType === "video" && { visibility: "hidden", bottom: -200 }),
            /**
             * add bottom padding only if user interacting to the play & pause state
             * other wise padding should not add at initial state of the render
             * */
            ...(isMediaPlaying !== null && mediaType === "video" && { paddingBottom: "3rem" })
          }}
          className={`entity-meta-details ${isLoading ? "stale" : ""}`}
        >
          <Box sx={{ flex: 1, pointerEvents: "auto" }}>
            <Typography variant='h5' color='grey.50' mb='0.25rem'>
              {item?.name}
            </Typography>
            <Typography className='scroller' variant='body1' color='grey.300' sx={{ maxHeight: "5rem", overflowY: "auto", pr: "0.185rem" }}>
              {item?.description || "No description."}
            </Typography>
          </Box>
          {!hideRelationshipButton && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 12, pointerEvents: "auto" }}>
              {hasWritePermissions && (
                <IconButton
                  onClick={handleEdit}
                  sx={{
                    color: "#FCFCFD",
                    bgcolor: "transparent",
                    width: 40,
                    height: 40,
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.25)"
                    }
                  }}
                >
                  <CreateOutlined sx={{ fontSize: "1.5rem" }} />
                </IconButton>
              )}
              {showRelatedBtn && (
                <Button
                  variant='contained'
                  disableElevation
                  sx={{ bgcolor: "grey.500", textTransform: "none", borderRadius: "0.5rem", py: 16 }}
                  onClick={onFindRelatedItem}
                  disabled={isLoading}
                >
                  {isLoading && <CircularProgress size={16} />} Find Related
                </Button>
              )}
            </Box>
          )}
        </Box>

        {/* Loading overlay*/}
        {(isLoading || (showLoadingOverlay && !isMediaUnavailable)) && (
          <LoadingIndicator
            message={`Loading ${mediaType !== "unknown" ? mediaType : "Media"}`}
            loaderSize={32}
            sx={{
              position: "absolute",
              height: "100%",
              width: "100%",
              top: 0,
              left: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              bgcolor: "#172b4973",
              backdropFilter: "blur(8px)"
            }}
          />
        )}
      </Box>
    </>
  );
}
